import { Box, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { Close as CloseIcon, CheckCircle as CheckIcon, Error as ErrorIcon, Info as InfoIcon } from '@mui/icons-material';
import { AnimatePresence, motion } from 'framer-motion';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

/**
 * Lightweight toast system. Avoids notistack's bundle weight; we only
 * need three tones and a single stacking column. The provider exposes
 * `useToast()` which returns `{ success, error, info }` shorthands —
 * keeps call sites readable.
 *
 * Auto-dismiss is per-toast (default 4s for success/info, 6s for error
 * because read-time matters more on errors). Click-anywhere or the
 * close icon dismisses early.
 */

type Tone = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  tone: Tone;
  title: ReactNode;
  body?: ReactNode;
}

interface ToastContextValue {
  push: (tone: Tone, title: ReactNode, body?: ReactNode) => void;
  success: (title: ReactNode, body?: ReactNode) => void;
  error: (title: ReactNode, body?: ReactNode) => void;
  info: (title: ReactNode, body?: ReactNode) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export function ToasterProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((tone: Tone, title: ReactNode, body?: ReactNode) => {
    const id = ++idRef.current;
    setToasts((prev) => [...prev, { id, tone, title, body }]);
  }, []);

  const value = useMemo<ToastContextValue>(() => ({
    push,
    success: (title, body) => push('success', title, body),
    error:   (title, body) => push('error',   title, body),
    info:    (title, body) => push('info',    title, body),
    dismiss,
  }), [push, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToasterOverlay toasts={toasts} dismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToasterProvider>');
  return ctx;
}

function ToasterOverlay({ toasts, dismiss }: { toasts: Toast[]; dismiss: (id: number) => void }) {
  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: { xs: 16, md: 24 },
        right: { xs: 16, md: 24 },
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        zIndex: 1400,
        pointerEvents: 'none',
        maxWidth: 380,
        width: '100%',
      }}
    >
      <AnimatePresence initial={false}>
        {toasts.map((t) => (
          <ToastCard key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </AnimatePresence>
    </Box>
  );
}

function ToastCard({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const theme = useTheme();
  const palette = (() => {
    switch (toast.tone) {
      case 'success': return { fg: theme.palette.status.success, bg: theme.palette.status.successBg, Icon: CheckIcon };
      case 'error':   return { fg: theme.palette.status.danger,  bg: theme.palette.status.dangerBg,  Icon: ErrorIcon };
      case 'info':    return { fg: theme.palette.status.info,    bg: theme.palette.status.infoBg,    Icon: InfoIcon };
    }
  })();

  useEffect(() => {
    const timeout = setTimeout(onDismiss, toast.tone === 'error' ? 6000 : 4000);
    return () => clearTimeout(timeout);
  }, [onDismiss, toast.tone]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 24, scale: 0.96 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 24, scale: 0.96 }}
      transition={{ duration: 0.22, ease: [0.2, 0, 0, 1] }}
      style={{ pointerEvents: 'auto' }}
    >
      <Box
        sx={{
          bgcolor: theme.palette.surface.card,
          border: `1px solid ${theme.palette.border.subtle}`,
          borderLeft: `3px solid ${palette.fg}`,
          borderRadius: theme.radius.md + 'px',
          boxShadow: theme.elevation.overlay,
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            bgcolor: palette.bg,
            color: palette.fg,
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
          }}
        >
          <palette.Icon sx={{ fontSize: 18 }} />
        </Box>
        <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" fontWeight={600} sx={{ color: 'text.primary' }}>
            {toast.title}
          </Typography>
          {toast.body && (
            <Typography variant="caption" color="text.secondary">
              {toast.body}
            </Typography>
          )}
        </Stack>
        <IconButton size="small" onClick={onDismiss} aria-label="dismiss notification">
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </motion.div>
  );
}
